import { atom, getDefaultStore, useAtomValue, type PrimitiveAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

/**
 * A small stale-while-revalidate cache on Jotai.
 *
 * Every API read used to live in component state, so navigating back to a page
 * (projects, a production) started from empty and waited on the network again.
 * Here each response lives in a Jotai atom keyed by a string, so:
 *  - returning to a page renders the cached data instantly while it refetches;
 *  - two components reading the same key share one request (and one poll);
 *  - a request never overlaps itself — the old setInterval polls piled up
 *    pending requests whenever the server was slower than the interval;
 *  - polls pause while the tab is hidden and refresh when it becomes visible.
 */

export interface QueryEntry<T> {
  data: T | undefined;
  /** ms epoch of the last successful fetch; 0 = never fetched. */
  updatedAt: number;
  error: unknown;
}

const EMPTY: QueryEntry<unknown> = { data: undefined, updatedAt: 0, error: null };
const store = getDefaultStore();
const atoms = new Map<string, PrimitiveAtom<QueryEntry<unknown>>>();
const inflight = new Map<string, Promise<unknown>>();

function queryAtom<T>(key: string): PrimitiveAtom<QueryEntry<T>> {
  let a = atoms.get(key);
  if (!a) {
    a = atom<QueryEntry<unknown>>(EMPTY);
    atoms.set(key, a);
  }
  return a as unknown as PrimitiveAtom<QueryEntry<T>>;
}

/** Fetch `key` now, joining a request already in flight for it. */
export function fetchQuery<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const running = inflight.get(key);
  if (running) return running as Promise<T>;
  const a = queryAtom<T>(key);
  const p = fetcher()
    .then((data) => {
      store.set(a, { data, updatedAt: Date.now(), error: null });
      return data;
    })
    .catch((error: unknown) => {
      store.set(a, (prev) => ({ ...prev, error }));
      throw error;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

/** Update cached data in place (optimistic updates, "load more" appends). */
export function setQueryData<T>(key: string, update: (prev: T | undefined) => T): void {
  store.set(queryAtom<T>(key), (prev) => ({ ...prev, data: update(prev.data) }));
}

/** Read cached data outside React (e.g. to seed state). */
export function getQueryData<T>(key: string): T | undefined {
  return store.get(queryAtom<T>(key)).data;
}

/** GET a JSON API route; throws on a non-2xx status so the cache keeps old data. */
export async function getJson<T>(fetchFn: () => Promise<Response>): Promise<T> {
  const resp = await fetchFn();
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return (await resp.json()) as T;
}

export interface QueryOptions {
  /** Poll every N ms while mounted and the tab is visible. Omit for no polling. */
  intervalMs?: number;
  /** On mount, skip the refetch if the cache is younger than this (default: always refetch). */
  staleMs?: number;
}

export interface QueryResult<T> {
  data: T | undefined;
  /** True only until the first response for this key — cached data is never "loading". */
  isLoading: boolean;
  error: unknown;
  /** ms epoch of the last successful fetch (0 = never) — e.g. to cache-bust thumbnails. */
  updatedAt: number;
  refetch: () => Promise<T | undefined>;
}

/**
 * Subscribe to a cached GET. `key` = null disables it. The fetcher may change
 * between renders; the latest one is always used.
 */
export function useQuery<T>(key: string | null, fetcher: () => Promise<T>, options: QueryOptions = {}): QueryResult<T> {
  const entry = useAtomValue(queryAtom<T>(key ?? '__disabled__'));
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const { intervalMs, staleMs = 0 } = options;

  const refetch = useCallback(() => (key ? fetchQuery(key, () => fetcherRef.current()) : Promise.resolve(undefined)), [key]);

  useEffect(() => {
    if (!key) return;
    const a = queryAtom<T>(key);
    const run = (minAgeMs: number) => {
      if (document.visibilityState === 'hidden') return;
      // Several subscribers polling one key converge on one request per period.
      if (Date.now() - store.get(a).updatedAt < minAgeMs) return;
      fetchQuery(key, () => fetcherRef.current()).catch(() => {
        /* keep the cached data; the next poll retries */
      });
    };
    run(staleMs);
    if (!intervalMs) return;
    const id = setInterval(() => run(intervalMs / 2), intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') run(intervalMs / 2);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [key, intervalMs, staleMs]);

  return {
    data: entry.data,
    isLoading: entry.updatedAt === 0 && !entry.error,
    error: entry.error,
    updatedAt: entry.updatedAt,
    refetch,
  };
}
